<template>
  <div class="container px-4">
    <div
      class="fs-7 text-start text-black"
      :innerHTML="t('PAYJOY_CARD_VIEWS.CC_CALCULATOR_DESCRIPTION')"
    />
    <br />
    <h1 id="spend-text">{{ t("PLAN_SPEND") }}</h1>
    <div class="input-container w-100 p-4">
      <div class="text-center text-black fs-2 input-display">
        <b>$</b>
        <input
          type="text"
          inputmode="decimal"
          pattern="[0-9,]*\.?[0-9]*"
          :value="formattedAmountInput"
          @change="handleInputChange($event.target.value)"
          :style="`width: ${formattedAmountInput.length * 16}px;`"
          @focus="() => (formattedAmountInput = '')"
        />
      </div>
      <div class="text-center fst-italic">
        {{
          t("PAYJOY_CARD_VIEWS.CC_CALCULATOR_AMOUNT_AVAILABLE", {
            CREDIT_AVAILABLE: currencyFormat(maxAmount),
          })
        }}
      </div>
    </div>
    <div v-if="estimateData !== null">
      <h2 id="payment-weeks" class="mb-2">
        {{ t("WEEK_PAYMENT_PLAN", { weeks: estimateData?.paymentTerm }) }}
      </h2>
      <div
        class="d-flex flex-column mt-2 justify-content-center align-content-between"
      >
        <div class="row w-100 mx-0 mb-3">
          <div class="col-8 text-start summary-titles">
            {{ t("WEEKLY_PAYMENT") }}
          </div>
          <div class="col-4 text-end summery-values pe-0">
            {{ currencyFormat(weeklyPayment) }}
          </div>
        </div>
        <div class="row w-100 mx-0 mb-3">
          <div class="col-8 text-start summary-titles">
            {{ t("INTEREST_RATE") }}
          </div>
          <div class="col-4 text-end summary-titles pe-0">
            {{
              country === CL_COUNTRY.CO
                ? formatNumber(calculateEffectiveInterestRate(estimateData.annualPercentageRate) * 100)
                : formatNumber(Number(estimateData.annualPercentageRate * 100))
            }}%
          </div>
        </div>
        <!--///////CO Fees///// TODO: Implement Settings service-->
        <div v-if="country === CL_COUNTRY.CO">
          <div class="row w-100 mx-0 mb-3">
            <div class="col-8 text-start summary-titles">
              {{ t("CL_WITHDRAWAL.AVAILABILITY_FEE") }}
            </div>
            <div class="col-4 text-end summary-titles pe-0">
              {{
                formatNumber(
                  Number(
                    estimateData.feeSchema?.availabilityFee
                      ?.rateIncludingTaxPercentage
                  )
                )
              }}%
            </div>
          </div>
          <div class="row w-100 mx-0 mb-3">
            <div class="col-8 text-start summary-titles">
              {{ t("CL_WITHDRAWAL.INSURANCE_FEE") }}
            </div>
            <div class="col-4 text-end summary-titles pe-0">
              {{
                formatNumber(
                  Number(
                    estimateData.feeSchema?.insuranceFee
                      ?.rateIncludingCommissionPercentage
                  )
                )
              }}%
            </div>
          </div>
        </div>
        <div class="row w-100 mx-0 mb-3">
          <div class="col-8 text-start summary-titles">
            {{ t("TOTAL_TO_PAY") }}
          </div>
          <div class="col-4 text-end summary-titles pe-0">
            {{ currencyFormat(estimateData.withdrawalTotalPayment) }}
          </div>
          <div class="text-start ps-0">
            <img
              alt="trending down"
              :src="useAsset('img/credit-line/credit-card/trending_down.svg')"
            />
            <span id="date-info">
              {{
                t("DATE_TO_LESS_INTEREST", {
                  date: formatDateToDDMM,
                })
              }}
            </span>
          </div>
        </div>
      </div>

      <Alert
        class="mt-2"
        alert-icon="fa fa-info-circle text-black"
        alert-type="alert-info-dark"
        :closable="false"
        :message="t('CALCULATOR_INFO_ALERT')"
        :message-font-style="messageFontStyle"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  watch,
  withDefaults,
  onBeforeMount,
  getCurrentInstance,
} from "vue";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import useAsset from "@/apps/credit-line/helpers/useAsset";
import { IWithdrawalEstimate } from "@/apps/credit-line/models/IWithdrawalEstimate";
import Alert from "@/shared-components/common/Alert.vue";
import { formatNumber } from "@/apps/credit-line/helpers/formatNumber.helper";
import currencyFormat from "@/apps/credit-line/helpers/currencyFormatter.helper";
import {
  getCountry,
  setLanguage,
} from "@/apps/credit-line/helpers/translations.helper";
import { CL_COUNTRY } from "@/apps/credit-line/types/cl_countries";
import { calculateEffectiveInterestRate } from "@/shared-helpers/effective-interest-rate.helper";

// Props
const props = withDefaults(
  defineProps<{
    estimateData: IWithdrawalEstimate | null;
    maxAmount?: number;
  }>(),
  {
    maxAmount: 0,
  }
);

// Emits
const emit = defineEmits<{
  (e: "onEstimateAmountChange", value: string): void;
}>();
const { t } = useI18n();
// Data
const country = ref<CL_COUNTRY>();
const amountInput = ref<number>(0.0);
const formattedAmountInput = ref<string>("0.00");
const weeklyPayment = computed(() => {
  const newMin = props.estimateData?.newMinimumPaymentAmount ?? 0;
  const currentMin = props.estimateData?.currentMinimumPaymentAmount ?? 0;
  return newMin - currentMin;
});

onBeforeMount(async () => {
  setLanguage(getCurrentInstance()?.proxy);
  country.value = await getCountry();
});

const messageFontStyle = {
  color: "#595A5A",
  "font-feature-settings": `'liga' off, 'clig' off`,
  "font-family": "Inter, serif",
  "font-size": "13px",
  "font-style": "normal",
  "font-weight": 400,
};

const formatDateToDDMM = computed(() => {
  if (!props.estimateData?.nextPaymentDueDate) return "";
  const date = new Date(props.estimateData.nextPaymentDueDate * 1000);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}`;
});

function handleInputChange(rawValue: string) {
  const cleanValue = rawValue.replace(/,/g, "");
  const numericValue = parseFloat(cleanValue);

  if (isNaN(numericValue)) return;

  let validValue = numericValue;

  if (validValue > props.maxAmount) validValue = props.maxAmount;
  if (validValue < 100) validValue = 100;

  amountInput.value = validValue;
  formattedAmountInput.value = formatNumber(validValue);
  emit("onEstimateAmountChange", validValue.toFixed(2));
}

watch(amountInput, (val) => {
  formattedAmountInput.value = formatNumber(val);
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/constants";
.item-icon {
  width: 48px;
  height: 48px;
}
.input-container {
  border-radius: 10px;
  border: 1px solid #eee;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;

  input {
    font-weight: bold;
    border: 0;
    min-width: 100px;
    &:focus {
      outline: none;
    }
  }
}
.info-box {
  padding: 20px;
  font-size: 12px;
  border-radius: 10px;
  margin-bottom: 20px;
  background-color: $pj-color-lighter-green;
  text-align: center;
}

#spend-text {
  color: #2b2b2b;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

#payment-weeks {
  text-align: start;
  color: var(--Typography-Text-1, #111);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
}

.summary-titles {
  color: #696969;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  padding: 0 !important;
}

.summery-values {
  color: var(--Typography-Text-1, #111);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  padding: 0 !important;
}

#date-info {
  color: var(--Black, #007c3b);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
}

* {
  font-family: "Inter", serif !important;
}
</style>
